<template>
  <HrPageLayout
    title="Skills inventory"
    subtitle="Consolidated view of skills and profile fields from submitted Skills Audit Assessments (non-draft), within your data scope."
    icon=""
    :breadcrumb-items="breadcrumbItems"
  >
    <div v-if="!allowed" class="sarep-access-denied">
      <i class="bx bx-lock-alt"></i>
      You do not have access to this report.
    </div>
    <div v-else class="sarep-shell">
      <section class="sarep-hero" aria-label="Report purpose">
        <p class="sarep-lead">
          Use this register for county-level skills profiling: training history, competencies, memberships, and deployment context.
          Filter by audit year, refresh after new submissions, and export for committee packs or workforce analytics.
        </p>
      </section>

      <header class="sarep-toolbar">
        <div class="sarep-toolbar-left">
          <label class="sarep-field-label">Audit year</label>
          <select v-model.number="auditYear" class="sarep-select" @change="page = 1; load()">
            <option v-for="y in yearOpts" :key="'y-' + y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div class="sarep-toolbar-actions">
          <button type="button" class="sarep-btn sarep-btn--outline" :disabled="loading" @click="load">
            <i class="bx bx-refresh"></i>
            Refresh
          </button>
          <button type="button" class="sarep-btn sarep-btn--primary" :disabled="loading" @click="exportCsv">
            <i class="bx bx-export"></i>
            Export CSV
          </button>
        </div>
      </header>

      <div class="sarep-table-card">
        <div v-if="loading" class="sarep-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
        <div v-else class="sarep-table-scroll">
          <table class="sarep-table">
            <thead>
              <tr>
                <th>SNO</th>
                <th>PERSONAL No.</th>
                <th>ID NO</th>
                <th>NAME</th>
                <th>DESIGNATION</th>
                <th>JOB GROUP</th>
                <th>DATE OF BIRTH</th>
                <th>PHONE NO</th>
                <th>ETHNICITY</th>
                <th>GENDER</th>
                <th>SPECIAL NEED</th>
                <th>YEARS OF EXPERIENCE</th>
                <th>HIGHEST ACADEMIC</th>
                <th>OTHER ACADEMICS</th>
                <th>TRAININGS</th>
                <th>PROFESSIONAL BODY</th>
                <th>MEMBERSHIP NO.</th>
                <th>HARD SKILLS</th>
                <th>SOFT SKILLS</th>
                <th>DEPARTMENT</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in rows" :key="'inv-' + page + '-' + idx + '-' + (r.id_no || r.personal_no)">
                <td>{{ r.sno }}</td>
                <td>{{ r.personal_no }}</td>
                <td>{{ r.id_no }}</td>
                <td class="sarep-strong">{{ r.name }}</td>
                <td>{{ r.designation }}</td>
                <td>{{ r.job_group }}</td>
                <td>{{ r.date_of_birth }}</td>
                <td>{{ r.phone_no }}</td>
                <td>{{ r.ethnicity }}</td>
                <td>{{ r.gender }}</td>
                <td class="sarep-cell-wrap">{{ r.special_need }}</td>
                <td>{{ r.years_experience }}</td>
                <td class="sarep-cell-wrap">{{ r.highest_academic }}</td>
                <td class="sarep-cell-wrap">{{ r.other_academics }}</td>
                <td class="sarep-cell-wrap">{{ r.trainings }}</td>
                <td class="sarep-cell-wrap">{{ r.professional_body }}</td>
                <td>{{ r.membership_no }}</td>
                <td class="sarep-cell-wrap">{{ r.hard_skills }}</td>
                <td class="sarep-cell-wrap">{{ r.soft_skills }}</td>
                <td>{{ r.department }}</td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="20" class="sarep-empty">No submitted assessments for this year.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="total > limit" class="sarep-pagination">
          <button type="button" class="sarep-btn sarep-btn--sm sarep-btn--outline" :disabled="page <= 1" @click="page--; load()">
            Previous
          </button>
          <span class="sarep-page-info">Page {{ page }} · showing {{ rows.length }} of {{ total }}</span>
          <button type="button" class="sarep-btn sarep-btn--sm sarep-btn--outline" :disabled="page * limit >= total" @click="page++; load()">
            Next
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload, getRight } from "@/api";

export default {
  name: "ModuleSkillsAuditInventory",
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      auditYear: y,
      yearOpts: [y + 1, y, y - 1, y - 2],
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      limit: 50,
      breadcrumbItems: [
        { label: "Home", route: "/dashboard" },
        { label: "County HRMIS (SR)" },
        { label: "Skills Audit" },
        { label: "Skills inventory" }
      ]
    };
  },
  computed: {
    allowed() {
      return getRight("SKILLS AUDIT SKILLS INVENTORY");
    }
  },
  mounted() {
    if (this.allowed) this.load();
  },
  methods: {
    load() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getSkillsAuditSkillsInventoryReport");
      fd.append("audit_year", String(this.auditYear));
      fd.append("page", String(this.page));
      fd.append("limit", String(this.limit));
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success && res?.data?.data) {
            const d = res.data.data;
            this.rows = Array.isArray(d.data) ? d.data : [];
            this.total = d.total || 0;
          } else {
            this.rows = [];
            this.total = 0;
          }
        })
        .catch(() => {
          this.rows = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async exportCsv() {
      const fd = new FormData();
      fd.append("function", "exportSkillsAuditSkillsInventoryReport");
      fd.append("audit_year", String(this.auditYear));
      try {
        const res = await executeDownload(fd, authUrl);
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `skills_inventory_${this.auditYear}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (_) {
        alert("Export failed.");
      }
    }
  }
};
</script>

<style scoped>
.sarep-shell {
  max-width: 100%;
  margin: 0 auto;
  padding-bottom: 36px;
}

.sarep-access-denied {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
}

.sarep-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 55%, #0f5980 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 10px 32px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.sarep-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
}

.sarep-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.sarep-toolbar-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.sarep-field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.sarep-select {
  min-width: 120px;
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  background: #fff;
}

.sarep-select:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.sarep-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sarep-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  border: none;
}

.sarep-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sarep-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}

.sarep-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}

.sarep-btn--sm {
  padding: 8px 14px;
  font-size: 12px;
}

.sarep-table-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(13, 79, 115, 0.07);
  overflow: hidden;
}

.sarep-table-scroll {
  overflow-x: auto;
  max-height: min(70vh, 900px);
  overflow-y: auto;
}

.sarep-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.sarep-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.sarep-table th {
  text-align: left;
  padding: 11px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.sarep-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: top;
}

.sarep-table tbody tr:hover {
  background: #fafbfc;
}

.sarep-strong {
  font-weight: 600;
  color: #0f172a;
}

.sarep-cell-wrap {
  max-width: 200px;
  white-space: normal;
  font-size: 12px;
  line-height: 1.4;
}

.sarep-empty {
  text-align: center;
  color: #94a3b8;
  padding: 32px 16px !important;
  font-size: 14px;
}

.sarep-loading {
  text-align: center;
  padding: 36px;
  color: #64748b;
  font-size: 14px;
}

.sarep-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sarep-page-info {
  font-size: 13px;
  color: #64748b;
}
</style>
